/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { isEqual } from 'lodash';
import { logError } from 'src/loggingManager';
import { updateAsset, createAsset } from 'src/services/assetsClient';
import { updateLink, getLinksForAsset } from 'src/services/linkClient';
import { generatePreviewUsingSku } from 'src/services/renderingClient';
import { createSceneVersion } from 'src/services/sceneClient';
import { uploadJSONFile, getUrlById } from 'src/services/uploadsClient';
import { apiPrefix } from 'src/settings';
import { parseError } from 'src/util/misc';
import { makeAuthenticatedServiceRequest } from '../../../../services/servicesClient';
import {
    ASSET_ACTIONS,
    PRODUCT_VARIABLE_TYPES,
    SCENE_VARIABLE_TYPES,
    MERCHANDISING,
    DOCUMENT_ACTIONS,
    SURFACE_ACTIONS,
    DEFAULT,
} from './constants';
import { BLANK_STATE } from './slice';
import { LinkType } from 'src/types/link';
import { LinkPurposeType } from 'src/types/sceneVariation';
import {
    SceneVariable_Document_EffectData,
    SceneVariable_DocumentConfigurationData,
    SceneVariable_DocumentData,
    SceneVariable_ImageData,
    SceneVariable_ImageData_Condition,
    SceneVariable_Rule_Value,
    SceneVariableCurrentLink,
} from 'src/types/sceneVariable';

const SCENE_ENDPOINT = `https://${apiPrefix}scenes.documents.cimpress.io`;

import type { SceneVariationScene, SaveSceneVariationAssetResult } from 'src/types/services';
import type { TenantType } from 'src/types';
import { getSdk } from 'src/sdk';

// Type definitions
type ScenePurpose = {
    label: string;
    value: string;
};

type AssetInfo = {
    label: string;
    value: string;
};

type RuleItem = {
    id: number;
    type?: string;
    asset?: AssetInfo;
    action?: string;
    file?: unknown;
    fillColor?: string;
    x0?: number;
    y0?: number;
    x1?: number;
    y1?: number;
};

type SceneVariable = {
    purpose: string;
    rules: RuleItem[];
};

type ProductConfigurationValue = {
    action: keyof typeof ASSET_ACTIONS | typeof DEFAULT;
    value?:
        | {
              name?: string;
              imageUrl?: string;
          }
        | string;
};

type ProductConfiguration = {
    variableOption: string;
    layer: AssetInfo | null;
    document: string | null;
    variableAttribute: string;
    values: Record<string, ProductConfigurationValue | SceneVariable_Rule_Value[] | string>;
};

type SurfaceConfiguration = {
    documents: string[];
    variableAttribute: string;
    values: Record<string, { action: string; page?: number }>;
};

type SceneData = {
    sku: string;
    skuVersion: string | number;
    fixedAttributesSelection: Record<string, any>;
    scenePurposes: ScenePurpose[];
    sceneVariables: SceneVariable[];
    productConfigurations: ProductConfiguration[];
    surfaceConfigurations: SurfaceConfiguration[];
    sceneVariableId?: string;
    sceneLinkId?: string;
    sceneId?: string;
    tags?: string[];
    view?: {
        name?: string;
        ordinal?: number;
    };
};

type SubReference = {
    id: string;
} | null;

export type AssetActionType = keyof typeof ASSET_ACTIONS | keyof typeof PRODUCT_VARIABLE_TYPES | typeof DEFAULT;
// Functions
function getOperation(assetAction: AssetActionType | undefined): string {
    switch (assetAction) {
        case ASSET_ACTIONS.REMOVE:
            return 'remove';
        case ASSET_ACTIONS.FILL:
        case ASSET_ACTIONS.REPLACE:
            return 'replace';
        case PRODUCT_VARIABLE_TYPES.blending:
            return DOCUMENT_ACTIONS.BLENDING_MODE;
        default:
            return 'none';
    }
}

function capitalize(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getAssetImage(bkgName: string): AssetInfo {
    return {
        value: '',
        label: bkgName,
    };
}

export function getSceneVariationContent({ id }: { id: string }): Promise<SceneVariable_DocumentConfigurationData> {
    return getSdk()
        .Assets.getContent(id, { 'Cache-Control': 'no-cache' })
        .then((content) => content as SceneVariable_DocumentConfigurationData)
        .catch((e) => {
            logError(`Failure to get scene content! ${parseError(e)}`);
            throw e;
        });
}

export function mapPurposes(scenePurposes: ScenePurpose[]): LinkPurposeType[] {
    return scenePurposes.map((pur) => {
        const inxPoint = pur.value.indexOf(':');
        if (inxPoint >= 0) {
            return { purpose: pur.value.substr(0, inxPoint), subPurpose: pur.value.substr(inxPoint + 1) };
        }

        return { purpose: pur.value, subPurpose: pur.value };
    });
}

export function mapScenePurposes(purposes: LinkPurposeType[] | undefined): ScenePurpose[] {
    return (purposes || []).map((item) => {
        if (item.purpose && item.subPurpose) {
            const value = `${item.purpose}:${item.subPurpose}`;
            if (item.purpose === MERCHANDISING.value) {
                return { label: `${MERCHANDISING.label} ${item.subPurpose}`, value };
            }
            return { label: `${capitalize(item.purpose)} ${capitalize(item.subPurpose)}`, value };
        }

        return { label: 'WRONG PURPOSE', value: '' };
    });
}

export function mapBlendingConditions(
    conditions: Record<string, SceneVariable_ImageData_Condition>,
): Record<string, string> {
    const output: Record<string, string> = {};
    Object.keys(conditions).forEach((item) => {
        output[item] = conditions[item].blendingMode || 'normal';
    });

    return output;
}

export function mapFinishConditions(conditions: Record<string, SceneVariable_ImageData_Condition>) {
    const output: Record<string, SceneVariable_Rule_Value[]> = {};
    Object.keys(conditions).forEach((item) => {
        output[item] = conditions[item].finish || [];
    });

    return output;
}

export function mapColorProfileConditions(
    conditions: Record<string, SceneVariable_ImageData_Condition>,
): Record<string, string> {
    const output: Record<string, string> = {};
    Object.keys(conditions).forEach((item) => {
        output[item] = conditions[item].colorProfile || DEFAULT;
    });

    return output;
}

function addDocumentCondition(
    documents: Record<string, SceneVariable_DocumentData[]>,
    docKey: string,
    docItem: SceneVariable_DocumentData,
) {
    if (documents[docKey]) {
        documents[docKey].push(docItem);
    } else {
        documents[docKey] = [docItem];
    }
}

export function mapSurfaceConditions(
    conditions: Record<string, SceneVariable_ImageData_Condition>,
): Record<string, { action: string; page?: number }> {
    const output: Record<string, { action: string; page?: number }> = {};

    Object.keys(conditions).forEach((item) => {
        const operationKey = conditions[item].operation?.toUpperCase() || '';
        const action = (SURFACE_ACTIONS as Record<string, string>)[operationKey];
        output[item] = action ? { action, page: conditions[item].page } : { action: DEFAULT };
    });

    return output;
}

// PARAMS
// sceneData: { sku, skuVersion, fixedAttributesSelection: { [string]: string },
// productConfigurations: [ { variableOption, layer: { value, label }, variableAttribute, values: { [string]: string } ],

export function mapDataToDoc(sceneId: string, sceneData: SceneVariableCurrentLink) {
    const productMapping: Record<string, SceneVariable_ImageData> = {};
    const sceneVariables: Record<string, SceneVariable_ImageData> = {};
    const documents: Record<string, SceneVariable_DocumentData[]> = {};
    const effects: SceneVariable_Document_EffectData[] = [];

    sceneData.sceneVariables.forEach((sceneVar) => {
        sceneVar.rules.forEach((rule) => {
            if (!rule || !rule.type) {
                return;
            }

            if (rule.type === SCENE_VARIABLE_TYPES['Asset Change'] && rule.asset) {
                const ruleLabel = rule.asset?.label || '';
                if (sceneVariables[ruleLabel]) {
                    sceneVariables[ruleLabel].conditions[sceneVar.purpose] = {
                        operation: getOperation(rule.action),
                    };
                } else {
                    sceneVariables[ruleLabel] = {
                        asset: rule.asset,
                        variable: {
                            type: 'purpose',
                        },
                        conditions: {
                            [sceneVar.purpose]: {
                                operation: getOperation(rule.action),
                            },
                        },
                    };
                }

                if (rule.action === ASSET_ACTIONS.FILL) {
                    sceneVariables[rule.asset.label].conditions[sceneVar.purpose].fillColor = rule.fillColor;
                } else if (rule.action === ASSET_ACTIONS.REPLACE) {
                    sceneVariables[rule.asset.label].conditions[sceneVar.purpose].file = rule.file;
                }
            } else {
                const cropping = effects.find((item) => item.variable.type === SCENE_VARIABLE_TYPES.Cropping);
                if (cropping) {
                    const effectConditions = cropping.conditions;
                    effectConditions[sceneVar.purpose] = {
                        crop: {
                            x: rule.x0 || 0,
                            y: rule.y0 || 0,
                            width: (rule.x1 || 0) - (rule.x0 || 0),
                            height: (rule.y1 || 0) - (rule.y0 || 0),
                        },
                    };
                } else {
                    effects.push({
                        variable: {
                            type: 'purpose',
                        },
                        conditions: {
                            [sceneVar.purpose]: {
                                crop: {
                                    x: rule.x0 || 0,
                                    y: rule.y0 || 0,
                                    width: (rule.x1 || 0) - (rule.x0 || 0),
                                    height: (rule.y1 || 0) - (rule.y0 || 0),
                                },
                            },
                        },
                    });
                }
            }
        });
    });

    sceneData.productConfigurations.forEach((conf) => {
        if (!conf) return;
        const conditions: Record<string, SceneVariable_ImageData_Condition> = {};

        switch (conf.variableOption) {
            case PRODUCT_VARIABLE_TYPES.product:
                Object.keys(conf.values).forEach((key) => {
                    let assetValue;
                    const value = conf.values[key].value;
                    if (value && typeof value !== 'string' && 'imageUrl' in value) {
                        assetValue = {
                            uri: conf.values[key].action === ASSET_ACTIONS.REPLACE ? value.imageUrl : undefined,
                            name: conf.values[key].action === ASSET_ACTIONS.REPLACE ? value.name : undefined,
                        };
                    } else if (typeof value === 'string') {
                        assetValue = {
                            color: conf.values[key].action === ASSET_ACTIONS.FILL ? value : undefined,
                        };
                    }
                    conditions[key] = {
                        operation: getOperation(conf.values[key].action),
                        asset: assetValue,
                    };

                    if (['none', 'remove'].includes(conditions[key].operation || '')) {
                        delete conditions[key].asset;
                    }
                });

                productMapping[conf.layer.label] = {
                    asset: conf.layer,
                    variable: {
                        type: 'attributes',
                        name: conf.variableAttribute,
                    },
                    conditions,
                };
                break;
            case PRODUCT_VARIABLE_TYPES.blending:
                Object.keys(conf.values).forEach((key) => {
                    const operation = getOperation(conf.variableOption);
                    conditions[key] = { [operation]: conf.values[key] };
                });

                documents[`${conf.document}-wrapper`] = [
                    {
                        variable: {
                            type: 'attributes',
                            name: conf.variableAttribute,
                        },
                        conditions,
                    },
                ];
                break;
            case PRODUCT_VARIABLE_TYPES.finishes:
                Object.keys(conf.values).forEach((key) => {
                    if (conf.values[key] && (conf.values[key] as any).length > 0) {
                        conditions[key] = { operation: 'add', finish: conf.values[key] as any };
                    } else {
                        conditions[key] = { operation: 'none' };
                    }
                });

                addDocumentCondition(documents, conf.document, {
                    variable: {
                        type: 'attributes',
                        name: conf.variableAttribute,
                    },
                    conditions,
                });
                break;
            case PRODUCT_VARIABLE_TYPES.colorProfile:
                Object.keys(conf.values).forEach((key) => {
                    const value = conf.values[key] as any;
                    if (value && value !== DEFAULT) {
                        conditions[key] = { operation: 'replace', colorProfile: value };
                    } else {
                        conditions[key] = { operation: 'none' };
                    }
                });

                addDocumentCondition(documents, conf.document, {
                    variable: {
                        type: 'attributes',
                        name: conf.variableAttribute,
                    },
                    conditions,
                });
                break;
            default:
                break;
        }
    });

    sceneData.surfaceConfigurations.forEach((conf) => {
        conf.documents.forEach((docKey) => {
            const conditions: Record<string, SceneVariable_ImageData_Condition> = {};
            Object.keys(conf.values).forEach((key) => {
                if (conf.values[key].action === DEFAULT) {
                    conditions[key] = { operation: 'none' };
                } else {
                    conditions[key] = {
                        operation: conf.values[key].action.toLowerCase(),
                        page: conf.values[key].page,
                    };
                }
            });

            const docItem: SceneVariable_DocumentData = {
                variable: {
                    type: 'attributes',
                    name: conf.variableAttribute,
                },
                conditions,
            };

            if (documents[docKey]) {
                documents[docKey].push(docItem);
            } else {
                documents[docKey] = [docItem];
            }
        });
    });

    return {
        sceneUri: `https://${apiPrefix}assets.documents.cimpress.io/v3/assets/${sceneId}/content`,
        linkPurposes: mapPurposes(sceneData.scenePurposes),
        images: { ...productMapping, ...sceneVariables },
        documents,
        effects,
    };
}

export function mapDocToData(
    output: SceneData,
    sceneConfigurationData: SceneVariable_DocumentConfigurationData,
): SceneData {
    const { images, effects, documents } = sceneConfigurationData;

    images &&
        Object.keys(images).forEach((imageKey) => {
            const data = images[imageKey];
            if (data.variable.type === 'attributes') {
                const mapToValues: Record<string, ProductConfigurationValue> = {};
                Object.keys(data.conditions).forEach((condition) => {
                    const item: ProductConfigurationValue = {
                        action: DEFAULT,
                    };

                    if (data.conditions[condition].asset && data.conditions[condition].asset?.uri) {
                        item.action = ASSET_ACTIONS.REPLACE;
                        item.value = {
                            name: data.conditions[condition].asset?.name,
                            imageUrl: data.conditions[condition].asset?.uri,
                        };
                    } else if (data.conditions[condition].asset && data.conditions[condition].asset?.color) {
                        item.action = ASSET_ACTIONS.FILL;
                        item.value = data.conditions[condition].asset?.color;
                    } else if (data.conditions[condition].operation === 'remove') {
                        item.action = ASSET_ACTIONS.REMOVE;
                    }

                    mapToValues[condition] = item;
                });
                output.productConfigurations.push({
                    variableOption: PRODUCT_VARIABLE_TYPES.product,
                    layer: data.asset,
                    document: null,
                    variableAttribute: data.variable.name || '',
                    values: { ...mapToValues },
                });
            } else if (data.variable.type === 'purpose') {
                Object.keys(data.conditions).forEach((purpose) => {
                    if (data.conditions[purpose].operation) {
                        const curPurposeVariable = output.sceneVariables.find((item) => item.purpose === purpose);
                        if (curPurposeVariable) {
                            const newItem: RuleItem = {
                                id: curPurposeVariable.rules.length,
                                type: SCENE_VARIABLE_TYPES['Asset Change'],
                                asset: data.asset,
                            };
                            if (data.conditions[purpose].file) {
                                newItem.action = ASSET_ACTIONS.REPLACE;
                                newItem.file = data.conditions[purpose].file;
                            } else if (data.conditions[purpose].fillColor) {
                                newItem.action = ASSET_ACTIONS.FILL;
                                newItem.fillColor = data.conditions[purpose].fillColor;
                            } else if (data.conditions[purpose].operation === 'remove') {
                                newItem.action = ASSET_ACTIONS.REMOVE;
                            }
                            curPurposeVariable.rules.push(newItem);
                        } else {
                            const sceneVar: SceneVariable = {
                                purpose,
                                rules: [
                                    {
                                        id: 0,
                                        type: SCENE_VARIABLE_TYPES['Asset Change'],
                                        asset: data.asset,
                                    },
                                ],
                            };
                            if (data.conditions[purpose].file) {
                                sceneVar.rules[0].action = ASSET_ACTIONS.REPLACE;
                                sceneVar.rules[0].file = data.conditions[purpose].file;
                            } else if (data.conditions[purpose].fillColor) {
                                sceneVar.rules[0].action = ASSET_ACTIONS.FILL;
                                sceneVar.rules[0].fillColor = data.conditions[purpose].fillColor;
                            } else if (data.conditions[purpose].operation === 'remove') {
                                sceneVar.rules[0].action = ASSET_ACTIONS.REMOVE;
                            }

                            output.sceneVariables.push(sceneVar);
                        }
                    }
                });
            }
        });

    documents &&
        Object.keys(documents).forEach((docKey) => {
            // data here can be an object (VariableScene V1) or an array of objects (VariableScene V2)
            let docData = documents[docKey];
            if (!docData) return;

            if (!Array.isArray(docData)) {
                docData = [docData];
            }

            docData.forEach((data) => {
                // product variable: blending mode
                if (Object.values(data.conditions).find((condition) => !!condition.blendingMode)) {
                    output.productConfigurations.push({
                        variableOption: PRODUCT_VARIABLE_TYPES.blending,
                        layer: null,
                        document: docKey.replace(/-wrapper/g, ''),
                        variableAttribute: data.variable?.name ?? '',
                        values: mapBlendingConditions(data.conditions),
                    });
                }

                // product variable: finish
                if (Object.values(data.conditions).find((condition) => !!condition.finish)) {
                    output.productConfigurations.push({
                        variableOption: PRODUCT_VARIABLE_TYPES.finishes,
                        layer: null,
                        document: docKey,
                        variableAttribute: data.variable?.name ?? '',
                        values: mapFinishConditions(data.conditions),
                    });
                }

                // product variable: color profile
                if (Object.values(data.conditions).find((condition) => !!condition.colorProfile)) {
                    output.productConfigurations.push({
                        variableOption: PRODUCT_VARIABLE_TYPES.colorProfile,
                        layer: null,
                        document: docKey,
                        variableAttribute: data.variable?.name ?? '',
                        values: mapColorProfileConditions(data.conditions),
                    });
                }

                // surface variable: page
                if (Object.values(data.conditions).find((condition) => !!condition.page)) {
                    const surfaceConfig = output.surfaceConfigurations.find(
                        (item) => item.variableAttribute === data.variable?.name,
                    );

                    const values = mapSurfaceConditions(data.conditions);
                    if (
                        surfaceConfig &&
                        isEqual(surfaceConfig.values, values) &&
                        !surfaceConfig.documents.includes(docKey)
                    ) {
                        surfaceConfig.documents.push(docKey);
                    } else {
                        output.surfaceConfigurations.push({
                            documents: [docKey],
                            variableAttribute: data.variable?.name ?? '',
                            values,
                        });
                    }
                }
            });
        });

    effects.forEach((effect) => {
        if (effect.variable.type === 'purpose') {
            Object.keys(effect.conditions).forEach((purpose) => {
                const curVariable = output.sceneVariables.find((item) => item.purpose === purpose);

                // Purpose config already exists
                if (curVariable) {
                    const cropConfig: RuleItem = {
                        id: curVariable.rules.length,
                        type: SCENE_VARIABLE_TYPES.Cropping,
                        x0: +effect.conditions[purpose].crop.x,
                        y0: +effect.conditions[purpose].crop.y,
                        x1: +effect.conditions[purpose].crop.x + effect.conditions[purpose].crop.width,
                        y1: +effect.conditions[purpose].crop.y + effect.conditions[purpose].crop.height,
                    };
                    curVariable.rules.push(cropConfig);
                } else {
                    const effectConfig: SceneVariable = {
                        purpose,
                        rules: [
                            {
                                id: 0,
                                type: SCENE_VARIABLE_TYPES.Cropping,
                                x0: +effect.conditions[purpose].crop.x,
                                y0: +effect.conditions[purpose].crop.y,
                                x1: +effect.conditions[purpose].crop.x + effect.conditions[purpose].crop.width,
                                y1: +effect.conditions[purpose].crop.y + effect.conditions[purpose].crop.height,
                            },
                        ],
                    };

                    output.sceneVariables.push(effectConfig);
                }
            });
        }
    });

    // Only map productVariables and SceneVariables as purposes are taking from the Link
    return output;
}

export function getLinkData(link: LinkType): Promise<SceneData> {
    if (!link.variableConfigurationId) {
        logError(`Link ${link.linkId} does not have variableConfigurationId`);
        return Promise.reject(new Error('Link does not have variableConfigurationId'));
    }

    const output: SceneData = {
        ...BLANK_STATE,
        sku: link.sku,
        skuVersion: link.skuVersion,
        fixedAttributesSelection: link.variables || {},
        scenePurposes: mapScenePurposes(link.linkPurposes),
        sceneVariableId: link.variableConfigurationId,
        sceneLinkId: link.linkId,
        sceneId: link.sceneId,
        tags: link.tags,
        productConfigurations: [],
        surfaceConfigurations: [],
        sceneVariables: [],
        view: link.view,
    };

    if (link._links && link._links.variableConfigurationContent && link._links.variableConfigurationContent.href) {
        return makeAuthenticatedServiceRequest({
            url: link._links.variableConfigurationContent.href,
            method: 'GET',
        }).then((res) => mapDocToData(output, res.data));
    }

    return getSceneVariationContent({ id: link.variableConfigurationId }).then((res) => mapDocToData(output, res));
}

// / params
// / res: { "count", "total", "offset", "_links": { "prev", "self": { "href" }, "next" }, "_embedded": { "item": [{ _links: { ...assetLinkData } }] }
export function findConfigLinks(res: LinkType[], variableConfigurationId = ''): LinkType | null {
    if (res && res.length > 0) {
        const links =
            variableConfigurationId !== ''
                ? res.filter((scene) => scene.variableConfigurationId === variableConfigurationId)
                : [];
        return links.length > 0 ? links[0] : null;
    }
    return null;
}

export function getSceneVariationLink({
    sceneId,
    variableConfigurationId,
}: {
    sceneId: string;
    variableConfigurationId: string;
}): Promise<LinkType | null> {
    return getLinksForAsset(getSdk(), { assetId: sceneId }).then((res) =>
        findConfigLinks(res, variableConfigurationId),
    );
}

function mapSceneToAsset(scene: SceneVariationScene) {
    const { name, description, notes, tags, sku, skuVersion, tenant, attributes, initialRevision, ...otherFields } =
        scene;
    return {
        name,
        description,
        notes,
        tags,
        referenceId: sku,
        referenceIdVersion: skuVersion,
        tenant,
        attributes,
        assetType: 'variableScene',
        ...otherFields,
    };
}

export function saveSceneVariationAsset({
    scene,
    configData,
    variableConfigurationId,
}: {
    scene: SceneVariationScene;
    configData: SceneVariableCurrentLink;
    variableConfigurationId?: string;
}): Promise<SaveSceneVariationAssetResult> {
    const dataToDoc = mapDataToDoc(scene.id, configData);
    const sdk = getSdk();
    if (variableConfigurationId) {
        return uploadJSONFile(sdk, dataToDoc)
            .then((uploadId) => {
                const initialVersion = { indexUri: getUrlById(uploadId) };
                return createSceneVersion(sdk, { sceneId: variableConfigurationId, version: initialVersion })
                    .then((newVersion: { id: string }) => {
                        const { id: _id, ...sceneData } = scene;
                        const updatedScene = Object.assign(
                            {},
                            sceneData,
                            { initialVersion },
                            { publishedRevisionId: newVersion.id },
                            { latestRevisionId: newVersion.id },
                        );
                        return updateAsset(getSdk(), {
                            tenantType: scene.tenant.type,
                            tenantId: scene.tenant.id,
                            assetId: variableConfigurationId,
                            newAsset: updatedScene as any,
                        })
                            .then((res) => res)
                            .catch((e) => {
                                logError(`Failure to Update scene! ${parseError(e)}`);
                                throw e;
                            });
                    })
                    .catch((e) => {
                        logError(`Failure to create scene version! ${parseError(e)}`);
                        throw e;
                    });
            })
            .catch((e) => {
                logError(`Failure to Upload JSON File! ${parseError(e)}`);
                throw e;
            });
    }
    return uploadJSONFile(sdk, dataToDoc)
        .then((uploadId) => {
            const assetInfo = mapSceneToAsset(scene);
            (assetInfo as any).initialRevision = {
                uri: getUrlById(uploadId),
            };
            return createAsset(getSdk(), {
                tenantType: scene.tenant.type,
                tenantId: scene.tenant.id,
                asset: assetInfo,
            })
                .then((res) => res)
                .catch((e) => {
                    logError(`Failure to import scene! ${parseError(e)}`);
                    throw e;
                });
        })
        .catch((e) => {
            logError(`Failure to Upload JSON File! ${parseError(e)}`);
            throw e;
        });
}

function checkLinkChanges(
    link: LinkType,
    configData: SceneVariableCurrentLink,
    subReferences: SubReference[],
): boolean {
    const skuChanged = link.referenceId !== configData.sku;
    const skuChangedVersion = link.referenceIdVersion !== configData.skuVersion;
    const subreferenceChanged =
        JSON.stringify(link.subReferenceIds) !== JSON.stringify(subReferences.map((sub) => (sub && sub.id) || null));
    const attributesChanged =
        JSON.stringify(link.attributes || link.variables) !== JSON.stringify(configData.fixedAttributesSelection);
    const purposesChanged = JSON.stringify(link.linkPurposes) !== JSON.stringify(configData.scenePurposes);

    return skuChanged || skuChangedVersion || subreferenceChanged || attributesChanged || purposesChanged;
}

export function updateLinkVariation({
    assetId,
    linkId,
    variableConfigurationId,
    configData,
    subReferences,
    tenant,
}: {
    assetId: string;
    linkId: string;
    variableConfigurationId: string;
    configData: SceneVariableCurrentLink;
    subReferences: SubReference[];
    tenant: TenantType;
}) {
    const linkData = {
        variableConfigurationId,
        referenceId: configData.sku,
        referenceIdVersion: configData.skuVersion,
        subReferenceIds: subReferences.map((ref) => ref && ref.id).filter(Boolean),
        attributes: configData.fixedAttributesSelection,
        linkPurposes: mapPurposes(configData.scenePurposes),
        tags: configData.tags,
        tenant,
        view: configData.view,
    };

    return updateLink(getSdk(), { linkInput: linkData as any, assetId, linkId }).catch((e) => {
        logError(`Failure to update link! ${parseError(e)}`);
        throw e;
    });
}

export function updateSceneVariation({
    link,
    scene,
    configData,
    subReferences,
    tenant,
}: {
    link: LinkType;
    scene: SceneVariationScene;
    configData: SceneVariableCurrentLink;
    subReferences: SubReference[];
    tenant: TenantType;
}): Promise<SaveSceneVariationAssetResult> {
    const linkHasChanges = checkLinkChanges(link, configData, subReferences);

    if (linkHasChanges) {
        return updateLinkVariation({
            assetId: scene.id,
            linkId: link.id || link.linkId,
            configData,
            tenant,
            subReferences,
            variableConfigurationId: configData.sceneVariableId,
        }).then(() =>
            saveSceneVariationAsset({ variableConfigurationId: link.variableConfigurationId, scene, configData }),
        );
    }

    return saveSceneVariationAsset({ variableConfigurationId: link.variableConfigurationId, scene, configData });
}

export function loadSceneVariations({ link }: { link?: LinkType }): Promise<SceneData> | Record<string, never> {
    if (link) {
        return getLinkData(link);
    }

    return {};
}

export function realizeScene({
    variationId,
    purposes,
    attributes,
    width,
    sku,
    skuVersion,
}: {
    variationId: string;
    purposes: LinkPurposeType[];
    attributes: Record<string, unknown>;
    width: number;
    sku: string;
    skuVersion: string | number;
}): Promise<string[]> {
    const data = {
        VariableAssetUri: `${SCENE_ENDPOINT}/v3/assets/${variationId}/content`,
        attributes,
        LinkPurposes: purposes,
    };
    const numSkuVersion = typeof skuVersion === 'string' ? parseInt(skuVersion, 10) : skuVersion;

    return getSdk()
        .VariableAssets.realize({ input: encodeURIComponent(JSON.stringify(data)) })
        .then((res) => {
            if (res) {
                return (res.results ?? [])
                    ?.map((scene) =>
                        generatePreviewUsingSku({
                            url: scene.variableAssetUrl!,
                            size: width,
                            useWidth: true,
                            sku,
                            skuVersion: numSkuVersion,
                            variables: attributes,
                        }),
                    )
                    .filter((url): url is string => url !== undefined);
            }
            return [];
        })
        .catch((e) => {
            logError(`Failure to realize scene! ${parseError(e)}`);
            throw e;
        });
}
